import React, { useMemo, useRef, useState } from "react";
import { geoOrthographic, geoPath, geoGraticule10 } from "d3-geo";
import { feature } from "topojson-client";
import worldData from "./world-110m.json";

const FEATURES = feature(worldData, worldData.objects.countries).features;

export default function Globe({ selected = [], onToggle, onHover, size = 440 }) {
  const [rot, setRot] = useState([-15, -18]);
  const drag = useRef(null);
  const moved = useRef(0);

  const projection = useMemo(
    () => geoOrthographic()
      .scale(size / 2 - 8)
      .translate([size / 2, size / 2])
      .rotate([rot[0], rot[1]]),
    [rot, size]
  );
  const path = useMemo(() => geoPath(projection), [projection]);
  const graticule = useMemo(() => path(geoGraticule10()), [path]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const onDown = (e) => {
    drag.current = { x: e.clientX, y: e.clientY, rot: [...rot] };
    moved.current = 0;
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    moved.current += Math.abs(dx) + Math.abs(dy);
    // Only start capturing (and treating as a drag-spin) once past the threshold,
    // so a genuine click on a country still fires its onClick handler.
    if (moved.current > 6) {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      const k = 0.4;
      setRot([
        drag.current.rot[0] + dx * k,
        Math.max(-85, Math.min(85, drag.current.rot[1] - dy * k)),
      ]);
    }
  };
  const onUp = (e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    drag.current = null;
  };

  const clickCountry = (name) => {
    if (moved.current > 6 || !name) return;
    onToggle?.(name);
  };

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${size} ${size}`}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={onUp}
      style={{ touchAction: "none", cursor: drag.current ? "grabbing" : "grab", maxWidth: size }}
      data-testid="manchi-globe"
      className="drop-shadow-[0_18px_40px_rgba(236,72,153,0.25)] select-none mx-auto"
    >
      <defs>
        <radialGradient id="manchi-ocean" cx="34%" cy="30%">
          <stop offset="0%" stopColor="#fff5fa" />
          <stop offset="70%" stopColor="#fbcfe2" />
          <stop offset="100%" stopColor="#f7b6d2" />
        </radialGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={size / 2 - 8} fill="url(#manchi-ocean)" stroke="#f472b6" strokeWidth="1.2" style={{ pointerEvents: "none" }} />
      <path d={graticule} fill="none" stroke="#f9c6dd" strokeWidth="0.4" style={{ pointerEvents: "none" }} />
      {FEATURES.map((f, i) => {
        const name = f.properties?.name;
        const d = path(f);
        if (!d) return null;
        const sel = selectedSet.has(name);
        return (
          <path
            key={i}
            d={d}
            data-testid={`globe-country-${(name || "").replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
            onClick={() => clickCountry(name)}
            onMouseEnter={(e) => !drag.current && onHover?.(name, e.clientX, e.clientY)}
            onMouseMove={(e) => !drag.current && onHover?.(name, e.clientX, e.clientY)}
            onMouseLeave={() => onHover?.(null)}
            fill={sel ? "#ec4899" : "#ffe1ef"}
            stroke={sel ? "#9d174d" : "#f5a3c7"}
            strokeWidth={sel ? 0.9 : 0.4}
            style={{ transition: "fill 0.15s, stroke 0.15s" }}
          >
            <title>{name}</title>
          </path>
        );
      })}
    </svg>
  );
}
